import { useFormikContext } from "formik";
import { useState } from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import { MultiSelect } from "@volca/ui/components/ui/multi-select";
import { Text } from "@volca/ui/components/ui/text";
import type {
  PayrollRosterFormValues,
  PayrollTeam,
} from "@/payroll/roster/types";
import { useRosterUsers } from "@/payroll/roster/use-roster-users";
import { rosterTeamSnapshots } from "@/payroll/teams/utils";
import { DrawerField } from "./drawer-field";

interface RosterMemberAccessFieldsProps {
  businessId: string;
  teams: PayrollTeam[];
  saving: boolean;
}

export const RosterMemberAccessFields = ({
  businessId,
  teams,
  saving,
}: RosterMemberAccessFieldsProps) => {
  const { values: member, setValues } =
    useFormikContext<PayrollRosterFormValues>();
  const { query: usersQuery, userOptions } = useRosterUsers(businessId);
  const [userSearch, setUserSearch] = useState("");
  return (
    <>
      <DrawerField label="Employee profile">
        <AutoComplete
          ariaLabel="Employee profile"
          selectedValue={member.user_id ?? ""}
          searchValue={userSearch}
          onSearchValueChange={setUserSearch}
          items={userOptions}
          onSelectedValueChange={(id) =>
            void setValues((current) => ({ ...current, user_id: id || null }))
          }
          isLoading={usersQuery.isLoading}
          disabled={saving || usersQuery.isError}
          fullWidth
          placeholder="Choose an employee"
        />
        <Text variant="caption" tone="muted">
          Choose the person who should receive this worker’s pay statements.
        </Text>
        {usersQuery.isError && (
          <Text variant="caption">
            Employee profiles could not be loaded.{" "}
            <Button
              variant="link"
              size="xs"
              className="h-auto p-0"
              onClick={() => void usersQuery.refetch()}
            >
              Try again
            </Button>
          </Text>
        )}
      </DrawerField>
      {teams.length > 0 && (
        <DrawerField label="Payroll teams">
          <MultiSelect
            label="Teams"
            options={teams}
            value={member.team_access_key_ids ?? []}
            disabled={saving}
            onChange={(ids) =>
              void setValues((current) => ({
                ...current,
                team_access_key_ids: ids,
                team_access_keys: rosterTeamSnapshots(
                  ids,
                  teams,
                  current.team_access_keys,
                ),
              }))
            }
          />
          <Text variant="caption" tone="muted">
            Each selected team can see this worker’s payroll content after
            publication. Unassigned workers are visible only to full-workbook
            users.
          </Text>
        </DrawerField>
      )}
    </>
  );
};
